import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { PinContainer, PinDot, PinCharacter, PinPosition } from './PINDisplay.styles';

// MAX_PIN_LENGTH inlined from apps/jagadwallet/tma/src/constants/pin.ts
const MAX_PIN_LENGTH = 6;

// TypeScript interface for component props
export interface PINDisplayProps {
  value: string;
  maxLength?: number;
  onComplete?: (value: string) => void;
  error?: boolean;
}

// Memoized individual pin position component
const PinPositionItem = memo(({
  displayChar,
  filled,
  error
}: {
  displayChar: string;
  filled: boolean;
  error?: boolean;
}) => (
  <PinPosition>
    {displayChar ? (
      <PinCharacter>{displayChar}</PinCharacter>
    ) : (
      <PinDot $filled={filled} $error={error} />
    )}
  </PinPosition>
));

PinPositionItem.displayName = 'PinPositionItem';

const PINDisplay: React.FC<PINDisplayProps> = ({
  value = '',
  maxLength = MAX_PIN_LENGTH,
  onComplete,
  error = false,
}) => {
  const [displayChars, setDisplayChars] = useState<string[]>(Array(maxLength).fill(''));
  const [filledPositions, setFilledPositions] = useState<boolean[]>(Array(maxLength).fill(false));
  const prevValueRef = useRef<string>('');
  const mainTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (mainTimerRef.current) {
        clearTimeout(mainTimerRef.current);
      }
    };
  }, []);

  // Use useCallback for timer management
  const hideAllCharacters = useCallback(() => {
    setDisplayChars(Array(maxLength).fill(''));
  }, [maxLength]);

  // Check for PIN completion
  useEffect(() => {
    if (value.length === maxLength && onComplete && value !== prevValueRef.current) {
      onComplete(value);
    }
  }, [value, maxLength, onComplete]);

  // Handle value changes including additions and deletions
  useEffect(() => {
    // Update filled positions based on current value length
    const valueLength = value.length;

    // Update filled positions based on current value length
    const newFilledPositions = Array(maxLength).fill(false);
    for (let i = 0; i < valueLength; i++) {
      newFilledPositions[i] = true;
    }
    setFilledPositions(newFilledPositions);

    // Clear any existing timer
    if (mainTimerRef.current) {
      clearTimeout(mainTimerRef.current);
      mainTimerRef.current = null;
    }

    // If there's a change in value (addition or deletion)
    if (value !== prevValueRef.current) {
      // Clear all displayed characters first
      setDisplayChars(() => {
        const updated = Array(maxLength).fill('');

        // If we have characters, show the last one
        if (valueLength > 0) {
          // Only show the last character
          updated[valueLength - 1] = value[valueLength - 1];
        }

        return updated;
      });

      // Set timer to hide all characters after 500ms
      if (valueLength > 0) {
        mainTimerRef.current = setTimeout(() => {
          hideAllCharacters();
          mainTimerRef.current = null;
        }, 500);
      }
    }

    // Update the previous value reference
    prevValueRef.current = value;
  }, [value, maxLength, hideAllCharacters]);

  // Handle reset when maxLength changes
  useEffect(() => {
    setDisplayChars(Array(maxLength).fill(''));
    setFilledPositions(Array(maxLength).fill(false));
    // Clear timer
    if (mainTimerRef.current) {
      clearTimeout(mainTimerRef.current);
      mainTimerRef.current = null;
    }
  }, [maxLength]);

  return (
    <PinContainer>
      {Array.from({ length: maxLength }).map((_, index) => (
        <PinPositionItem
          key={index}
          displayChar={displayChars[index]}
          filled={filledPositions[index]}
          error={error}
        />
      ))}
    </PinContainer>
  );
};

export default React.memo(PINDisplay);
